import type { ApiCredentialDto, ApiCredentialSecretsDto } from '@cartly/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  TextAreaField,
  TextField,
  useConfirm,
  useToast,
} from '@cartly/ui';
import { KeyRound, Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../api/client';
import { CopyField } from '../../components/Common';
import { formatRelative } from '../../app/format';
import { useSubmit } from '../../app/hooks';

export function SecretsModal({
  secrets,
  onClose,
}: {
  secrets: ApiCredentialSecretsDto;
  onClose: () => void;
}) {
  return (
    <Modal
      open
      title="Copy your new key now"
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          I've stored it safely
        </Button>
      }
    >
      <div className="stack">
        <Alert tone="warning">
          These values are shown only once. Store them in your server's secret manager or
          environment variables — never in source code or front-end bundles.
        </Alert>
        <CopyField
          label={secrets.credential.kind === 'secret' ? 'API key' : 'Publishable key'}
          value={secrets.apiKey}
          secret
        />
        {secrets.signingSecret && (
          <CopyField label="Signing secret" value={secrets.signingSecret} secret />
        )}
      </div>
    </Modal>
  );
}

export function CreateKeyModal({
  storeId,
  defaultKind,
  onClose,
  onCreated,
}: {
  storeId: string;
  defaultKind: 'secret' | 'publishable';
  onClose: () => void;
  onCreated: (s: ApiCredentialSecretsDto) => void;
}) {
  const [kind, setKind] = useState(defaultKind);
  const [name, setName] = useState(defaultKind === 'secret' ? 'Server' : 'Storefront');
  const [origins, setOrigins] = useState('');
  const submit = useSubmit();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const body =
      kind === 'secret'
        ? { kind, name }
        : {
            kind,
            name,
            allowedOrigins: origins
              .split(/[\s,]+/)
              .map((o) => o.trim())
              .filter(Boolean),
          };
    const created = await submit.run(() =>
      api.post<ApiCredentialSecretsDto>(`/stores/${storeId}/credentials`, body),
    );
    if (created) onCreated(created);
  };

  return (
    <Modal
      open
      title="Create API key"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="key-form" loading={submit.pending}>
            Create key
          </Button>
        </>
      }
    >
      <form id="key-form" className="stack" onSubmit={onSubmit} noValidate>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <fieldset className="choice-group">
          <legend className="field__label">Key type</legend>
          <label className="choice">
            <input
              type="radio"
              name="kind"
              checked={kind === 'secret'}
              onChange={() => setKind('secret')}
            />
            <span>
              <strong>Secret key</strong>
              <span className="field__hint">For your server or SDK. Full event access.</span>
            </span>
          </label>
          <label className="choice">
            <input
              type="radio"
              name="kind"
              checked={kind === 'publishable'}
              onChange={() => setKind('publishable')}
            />
            <span>
              <strong>Publishable key</strong>
              <span className="field__hint">
                For the browser tracker. Cart and checkout events only.
              </span>
            </span>
          </label>
        </fieldset>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={submit.fieldErrors['name']}
        />
        {kind === 'publishable' && (
          <TextAreaField
            label="Allowed storefront origins"
            placeholder="https://shop.example.com"
            value={origins}
            onChange={(e) => setOrigins(e.target.value)}
            hint="One per line. Requests from any other site are rejected."
            error={submit.fieldErrors['allowedOrigins'] ?? submit.fieldErrors['allowedOrigins.0']}
          />
        )}
      </form>
    </Modal>
  );
}

function keyState(k: ApiCredentialDto): { label: string; tone: 'success' | 'warning' | 'neutral' } {
  if (k.revokedAt) return { label: 'Revoked', tone: 'neutral' };
  if (k.expiresAt) {
    return new Date(k.expiresAt) > new Date()
      ? { label: `Rotated — expires ${formatRelative(k.expiresAt)}`, tone: 'warning' }
      : { label: 'Expired', tone: 'neutral' };
  }
  return { label: 'Active', tone: 'success' };
}

export function StoreKeys({
  storeId,
  keys,
  canManage,
  onChanged,
  onCreate,
}: {
  storeId: string;
  keys: ApiCredentialDto[];
  canManage: boolean;
  onChanged: (secrets?: ApiCredentialSecretsDto) => void;
  onCreate: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();

  const rotate = async (k: ApiCredentialDto) => {
    const ok = await confirm({
      title: `Rotate "${k.name}"?`,
      message: 'A new key is issued. The current key keeps working for 24 hours.',
      confirmLabel: 'Rotate key',
    });
    if (!ok) return;
    try {
      onChanged(
        await api.post<ApiCredentialSecretsDto>(`/stores/${storeId}/credentials/${k.id}/rotate`),
      );
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const revoke = async (k: ApiCredentialDto) => {
    const ok = await confirm({
      title: `Revoke "${k.name}"?`,
      message: 'Integrations using this key stop working immediately.',
      confirmLabel: 'Revoke key',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.post(`/stores/${storeId}/credentials/${k.id}/revoke`);
      toast.success('Key revoked');
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Card
      title="API keys"
      actions={
        canManage && (
          <Button variant="primary" icon={<Plus size={16} />} onClick={onCreate}>
            Create key
          </Button>
        )
      }
      flush
    >
      {keys.length === 0 ? (
        <EmptyState icon={<KeyRound size={22} />} title="No keys yet">
          Create a secret key for your server or a publishable key for the browser tracker.
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Access</th>
                <th>Last used</th>
                <th>Status</th>
                <th className="table__actions">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const state = keyState(k);
                const live = state.label === 'Active';
                return (
                  <tr key={k.id}>
                    <td>
                      <div className="table__primary">{k.name}</div>
                      <div className="table__secondary">
                        {k.kind === 'secret' ? 'Secret key' : 'Publishable key'}
                      </div>
                    </td>
                    <td className="mono text-sm">{k.keyPrefix}…</td>
                    <td className="text-sm">
                      {k.kind === 'secret'
                        ? k.scopes.join(', ')
                        : (k.allowedOrigins ?? []).join(', ')}
                    </td>
                    <td className="text-muted text-sm">
                      {k.lastUsedAt ? formatRelative(k.lastUsedAt) : 'Never'}
                    </td>
                    <td>
                      <Badge tone={state.tone}>{state.label}</Badge>
                    </td>
                    <td className="table__actions">
                      {canManage && live && (
                        <div className="row row--end">
                          <Button size="sm" variant="ghost" onClick={() => void rotate(k)}>
                            Rotate
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => void revoke(k)}>
                            Revoke
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
